import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import { Send as SendIcon, Info as InfoIcon } from '@mui/icons-material';
import api from '../services/api';

const validPlanNames = ['Free', 'Agency Free', 'Individual', 'Elite Individual', 'Professional', 'Agency Premium'];

const AdminUsageTips = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState<string>('admins');
  const [selectedPlans, setSelectedPlans] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState<any>(null);

  const handlePlanToggle = (plan: string) => {
    setSelectedPlans((prev) =>
      prev.includes(plan)
        ? prev.filter((p) => p !== plan)
        : [...prev, plan]
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    if (!message.trim()) {
      setError('Message is required');
      return;
    }

    try {
      setSending(true);
      setError(null);
      setSuccess(false);
      setResult(null);

      const payload: any = {
        title: title.trim(),
        message: message.trim(),
        targetAudience: targetAudience,
      };

      if (selectedPlans.length > 0) {
        payload.planNames = selectedPlans;
      }

      const response = await api.post('/admin/usage-tips', payload);

      if (response.data.success) {
        setSuccess(true);
        setResult(response.data);
        // Reset form
        setTitle('');
        setMessage('');
        setSelectedPlans([]);
        setTargetAudience('admins');
      } else {
        setError(response.data.error || 'Failed to send usage tips');
      }
    } catch (err: any) {
      console.error('Error sending usage tips:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.message ||
        'Failed to send usage tips. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Send Usage Tips
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Send branded Workflow.Estate email tips to agent admins or all users. Emails are queued and sent in the background, so you can continue working immediately.
        </Typography>

        <Grid container spacing={3}>
          {/* Form Section */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  {/* Title */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      required
                      placeholder="e.g., New Feature Available"
                      helperText="A brief title for the usage tip"
                    />
                  </Grid>

                  {/* Message */}
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Enter only the tip content; greeting and closing are added automatically.
                    </Typography>
                    <TextField
                      fullWidth
                      label="Message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      multiline
                      rows={6}
                      placeholder="Enter your usage tip message here..."
                      helperText="Tip body only (no greeting or signature needed)"
                    />
                  </Grid>

                  {/* Target Audience */}
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Target Audience</InputLabel>
                      <Select
                        value={targetAudience}
                        onChange={(e) => setTargetAudience(e.target.value)}
                        label="Target Audience"
                      >
                        <MenuItem value="admins">Agent Admins Only</MenuItem>
                        <MenuItem value="all">All Users</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Plan Filter */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Filter by Plans (Optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Leave empty to send to all plans, or select specific plans to target
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {validPlanNames.map((plan) => (
                        <Chip
                          key={plan}
                          label={plan}
                          onClick={() => handlePlanToggle(plan)}
                          color={selectedPlans.includes(plan) ? 'primary' : 'default'}
                          variant={selectedPlans.includes(plan) ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                    {selectedPlans.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          Selected: {selectedPlans.join(', ')}
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setSelectedPlans([])}
                          sx={{ ml: 1 }}
                        >
                          Clear
                        </Button>
                      </Box>
                    )}
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                        disabled={sending || !title.trim() || !message.trim()}
                        size="large"
                      >
                        {sending ? 'Sending...' : 'Send Usage Tips'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </Paper>
          </Grid>

          {/* Info Section */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InfoIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">How it works</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" paragraph>
                  <strong>Target Audience:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • <strong>Agent Admins:</strong> Sends to users with admin role only
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  • <strong>All Users:</strong> Sends to all active users in the system
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" paragraph>
                  <strong>Plan Filtering:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Select specific plans to target only agencies with those subscription plans. Leave empty to send to all plans.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" paragraph>
                  <strong>Delivery:</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Usage tips are delivered as branded emails to the selected recipients.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Result Section */}
          {result && (
            <Grid item xs={12}>
              <Paper sx={{ p: 3, bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h6" gutterBottom>
                  ✅ {result.message}
                </Typography>
                <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">Target Audience:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {Array.isArray(result.summary.targetAudience)
                        ? result.summary.targetAudience.join(', ')
                        : result.summary.targetAudience}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">Agencies Targeted:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {result.summary.totalAgencies}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">Emails Queued:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {result.summary.emailsQueued ?? result.summary.totalUsers ?? 0}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2">Delivery Status:</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {result.summary.emailsSent != null
                        ? `${result.summary.emailsSent} sent`
                        : 'Sending in background'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* Error Snackbar */}
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Success Snackbar */}
        <Snackbar
          open={success && !result}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Usage tip emails are being sent in the background.
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AdminUsageTips;

